import { apiFetch } from './api';

/** Génère le devis d'une demande. La demande passe automatiquement « traité ». */
export function genererDevis(contactId, body = {}) {
  return apiFetch(`/contacts/${contactId}/devis`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Crée un devis à la main, sans IA.
 * Recours lorsque la génération renvoie 422 (aucune formation exploitable).
 */
export function creerDevisManuel(contactId, body) {
  return apiFetch(`/contacts/${contactId}/devis/manuel`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Valide le devis : produit le PDF, l'attache à la demande et fige le devis. */
export function validerDevis(id, body = {}) {
  return apiFetch(`/devis/${id}/valider`, { method: 'POST', body: JSON.stringify(body) });
}

/** Envoie le devis par email (PDF en pièce jointe) — doit avoir été validé au préalable. */
export function envoyerDevis(id, body = {}) {
  return apiFetch(`/devis/${id}/envoyer`, { method: 'POST', body: JSON.stringify(body) });
}

/** L'email du devis tel qu'il partirait : objet, corps, destinataire, pièces. */
export function apercuEnvoiDevis(id) {
  return apiFetch(`/devis/${id}/envoyer/apercu`);
}

/** Devis d'une demande — `data` vaut null si aucun devis n'a encore été généré. */
export function getDevisParContact(contactId) {
  return apiFetch(`/contacts/${contactId}/devis`);
}

/** Les paramètres vides ne sont pas transmis : le serveur applique ses défauts. */
function versQuery(params) {
  const query = new URLSearchParams();
  for (const [cle, valeur] of Object.entries(params)) {
    if (valeur !== undefined && valeur !== null && valeur !== '') {
      query.set(cle, valeur);
    }
  }
  const suffixe = query.toString();
  return suffixe ? `?${suffixe}` : '';
}

/** Tous les devis générés, paginés. */
export function getDevis(params = {}) {
  return apiFetch(`/devis${versQuery(params)}`);
}

/**
 * Devis qu'on peut présenter à la signature d'un client présent (page
 * Clients > Signature devis). Mêmes paramètres que getDevis, sans les
 * facettes de suivi : recherche, période d'émission, tri, pagination.
 */
export function getDevisASigner(params = {}) {
  return apiFetch(`/devis/a-signer${versQuery(params)}`);
}

/**
 * Ouvre la signature d'un devis pour un client présent : le serveur rend le
 * jeton du lien de réponse, avec lequel on atteint la page de signature —
 * la même que celle du lien reçu par email, sans email.
 */
export function ouvrirSignatureDevis(id) {
  return apiFetch(`/devis/${id}/lien-signature`, { method: 'POST' });
}

export function getDevisById(id) {
  return apiFetch(`/devis/${id}`);
}

/**
 * Réponse publique du client au devis (lien reçu par email, sans
 * authentification : le jeton fait foi).
 */
export function getDevisParToken(token) {
  return apiFetch(`/devis/reponse/${token}`);
}

export function accepterDevis(token) {
  return apiFetch(`/devis/reponse/${token}/accepter`, { method: 'POST' });
}

export function refuserDevis(token) {
  return apiFetch(`/devis/reponse/${token}/refuser`, { method: 'POST' });
}

/** Signature électronique (image PNG en data URL tracée sur le canvas) — vaut acceptation. */
export function signerDevis(token, imageDataUrl, informations = null) {
  return apiFetch(`/devis/reponse/${token}/signature`, {
    method: 'POST',
    body: JSON.stringify({ image: imageDataUrl, informations }),
  });
}

/**
 * Signature du responsable ECHO CSE (image PNG en data URL tracée sur le
 * canvas) — préalable requis avant de confirmer la validation interne.
 */
export function signerDevisEchoCse(id, imageDataUrl) {
  return apiFetch(`/devis/${id}/signature-echo`, {
    method: 'POST',
    body: JSON.stringify({ image: imageDataUrl }),
  });
}

/** Les montants ne sont jamais envoyés : le serveur les recalcule. */
/** Supprime un devis, avec son contrat et ses journées s'il en a. La demande reste. */
export function deleteDevis(id) {
  return apiFetch(`/devis/${id}`, { method: 'DELETE' });
}

export function updateDevis(id, data) {
  return apiFetch(`/devis/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}

/**
 * URL du PDF. Le serveur le produit à la volée s'il n'existe pas encore ;
 * toute modification du devis invalide le fichier stocké.
 */
export function urlPdfDevis(id, { forcer = false, apercu = false, token = null } = {}) {
  const base = process.env.REACT_APP_API_URL || '/api';
  const params = [];
  if (forcer) params.push('forcer=1');
  if (apercu) params.push('apercu=1');
  return `${base}${token ? `/devis/reponse/${token}/pdf` : `/devis/${id}/pdf`}${params.length ? `?${params.join('&')}` : ''}`;
}

/** Nom de fichier annoncé par le serveur dans Content-Disposition. */
function nomDepuisEntete(entete, defaut) {
  const trouve = /filename="?([^"]+)"?/.exec(entete || '');
  return trouve ? trouve[1] : defaut;
}

/**
 * Télécharge le PDF et déclenche l'enregistrement côté navigateur.
 *
 * Passe par fetch plutôt qu'un simple lien : le rendu peut prendre plusieurs
 * secondes (Chromium), et surtout on a besoin de savoir quand il est terminé
 * pour rafraîchir le devis — c'est ce téléchargement qui renseigne pdf_path
 * et rattache donc le fichier à la demande.
 */
export async function telechargerPdfDevis(id, { forcer = false, token = null } = {}) {
  // credentials : le cookie de session part avec l'appel (cf. api.js).
  const res = await fetch(urlPdfDevis(id, { forcer, token }), { credentials: 'include' });

  if (!res.ok) {
    let message = `Génération du PDF impossible (${res.status})`;
    try {
      const corps = await res.json();
      if (corps?.message) message = corps.message;
    } catch {
      /* réponse non JSON : on garde le code HTTP */
    }
    const err = new Error(message);
    err.status = res.status;
    throw err;
  }

  const nom = nomDepuisEntete(res.headers.get('Content-Disposition'), `devis-${id}.pdf`);
  const blob = await res.blob();

  const url = URL.createObjectURL(blob);
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = nom;
  document.body.appendChild(lien);
  lien.click();
  lien.remove();
  URL.revokeObjectURL(url);

  return nom;
}


/**
 * Refait le PDF du devis (bouton « Régénérer le PDF » de la fiche). Sans
 * téléchargement : le fichier est simplement remplacé côté serveur.
 */
export function regenererPdfDevis(id) {
  return apiFetch(`/devis/${id}/pdf/regenerer`, { method: 'POST' });
}

/**
 * Téléverse le PDF d'un devis établi hors de l'application : le devis passe
 * en origine « externe » et ce fichier fait foi.
 */
export async function televerserDevisExterne(id, fichier) {
  const base = process.env.REACT_APP_API_URL || '/api';
  const donnees = new FormData();
  donnees.append('fichier', fichier, fichier.name);
  // Pas de Content-Type : le navigateur pose le multipart et sa frontière.
  const res = await fetch(`${base}/devis/${id}/fichier-externe`, {
    method: 'POST',
    body: donnees,
    credentials: 'include',
  });
  const corps = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(corps?.message || `Téléversement impossible (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return corps;
}
